import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { ExperienceCard } from './ExperienceCard';
import { ArrowRight } from 'lucide-react';
import { fetchClickStats, rankByClicks, rankByClicksWithMinLocation } from '../lib/popularExperiences';

// Travel-essentials cards never appear in the Top Experiences row —
// they're confined to their dedicated category page. Internal slug
// `transfers`, UI alias `rental_transfers`, URL alias `travel-essentials`.
const EXCLUDED_TOP_CATEGORIES = new Set([
  'transfers', 'rental_transfers', 'travel-essentials',
]);
const hasExcludedTopCategory = (exp) =>
  Array.isArray(exp.categories) &&
  exp.categories.some((c) => EXCLUDED_TOP_CATEGORIES.has(c));

export function ExperiencesSection({ experiences, onClear, activeCategory, activeDestination, searchQuery }) {
  const { lang, t } = useLanguage();
  const navigate = useNavigate();
  const hasFilters = activeCategory || activeDestination || searchQuery;

  const [clickStats, setClickStats] = useState(null);
  useEffect(() => {
    let alive = true;
    fetchClickStats().then((stats) => { if (alive) setClickStats(stats); });
    return () => { alive = false; };
  }, []);

  // When viewing search/filter results we keep the caller-provided ordering
  // so users can still see what matches their query. Otherwise we rank the
  // homepage row by total clicks (excluding limited-time events) and shuffle
  // the top 7 for visual variety on every page load. The homepage row also
  // guarantees at least 3 Sitges cards so the brand's home destination
  // always stays visible regardless of global click patterns.
  // Travel-essentials cards never appear in the Top Experiences row —
  // they're confined to their dedicated category page. Internal slug
  // `transfers`, UI alias `rental_transfers`, URL alias `travel-essentials`.

  const displayedExperiences = useMemo(() => {
    if (hasFilters) return experiences.slice(0, 7);
    if (clickStats === null) return [];
    const pool = (experiences || []).filter(
      (exp) => !exp.limited_event && !hasExcludedTopCategory(exp),
    );
    return rankByClicksWithMinLocation(pool, clickStats, {
      count: 7,
      shuffle: true,
      cacheKey: 'top-experiences',
      location: 'sitges',
      minFromLocation: 3,
    });
  }, [experiences, hasFilters, clickStats]);

  return (
    <section className="py-[30px] sm:py-[40px]" data-testid="top-experiences-section" id="experiences">
      <div className="max-w-[1280px] mx-auto px-5 lg:px-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="h-font text-[#0F3B63] text-[29px] sm:text-[38px] font-bold tracking-tight">
              {hasFilters ? t('search_results_title') : t('experiences_title')}
            </h2>
            {hasFilters && (
              <p className="text-sm text-muted-foreground mt-1">
                {t('showing_results', { count: experiences.length })}
              </p>
            )}
          </div>
          {hasFilters && (
            <button
              onClick={onClear}
              className="text-sm text-primary font-medium hover:underline"
              data-testid="clear-filters-button"
            >
              {t('clear_filters')}
            </button>
          )}
        </div>
      </div>

      <div className="max-w-[1280px] mx-auto">
        {displayedExperiences.length === 0 ? (
          <div className="mt-10 text-center py-16 px-5">
            <p className="text-muted-foreground text-lg">{t('no_results')}</p>
          </div>
        ) : (
          <div className="overflow-x-auto pt-6 pb-6 mt-3 no-scrollbar lg:overflow-visible lg:pt-0 lg:pb-4 lg:mt-7">
            <div className="flex gap-4 snap-x snap-mandatory pl-5 pr-5 lg:grid lg:grid-cols-4 lg:gap-5 lg:snap-none lg:pl-6 lg:pr-6">
            {displayedExperiences.map((exp, i) => (
              <ExperienceCard
                key={exp.id}
                exp={exp}
                testIdPrefix="experience"
                containerClassName="min-w-[297px] sm:min-w-[296px] h-[420px] snap-start flex-shrink-0 lg:min-w-0"
                animationDelay={i * 0.05}
              />
            ))}
            
            {/* Arrow card linking to Explore page */}
            <div className="min-w-[297px] sm:min-w-[296px] h-[420px] snap-start flex-shrink-0 lg:min-w-0 rounded-2xl flex flex-col items-center justify-center gap-4">
              <button
                onClick={() => navigate('/Explore')}
                className="w-20 h-20 rounded-full bg-[#3c84ff] hover:bg-[#2a6fe0] flex items-center justify-center transition-all duration-300 hover:scale-110"
              >
                <ArrowRight className="w-10 h-10 text-white" strokeWidth={2.5} />
              </button>
              <span className="text-[#0F3B63] font-bold text-lg" style={{ fontFamily: "'Outfit', sans-serif" }}>
                {t('view_all_experiences')}
              </span>
            </div>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
